import { AppLayout } from 'layouts'
import PropTypes from 'prop-types'
import React from 'react'
import { Button, Card, Col, Container, Row } from 'react-bootstrap'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { iconForCc, toPrice } from 'utils'

export function OrderDetails({ fundraiser, order }) {
  const [copied, setCopied] = React.useState(false)
  const { shortcode } = useParams()
  const rewardClassification = (fundraiser.reward_classification || 'reward').toUpperCase()
  const card = order.payment.charges.data[0].payment_method_details.card
  const shareUrl = `${new URL(window.location.href).origin}/s/${shortcode}/`
  const supportEmail = `${process.env.REACT_APP_SUPPORT_EMAIL}`
  const supportEmailUrl = `mailto:${supportEmail}?subject=Support%20Request%20%E2%80%93%20Order%20%23%20${order.id}&amp;body=Please%20tell%20us%20below%20how%20we%20can%20help%20resolve%20any%20issues%20with%20your%20order.%0A`
  const salesEmail = (fundraiser.user_email_address || null)
  const salesEmailUrl = `mailto:${salesEmail}?subject=Support%20Request%20%E2%80%93%20Order%20%23%20${order.id}&amp;body=Please%20tell%20us%20below%20how%20we%20can%20help%20resolve%20any%20issues%20with%20your%20order.%0A`

  const copyToClipboard = () => {
    const input = document.createElement('input')
    input.id = 'shareUrl'
    input.value = shareUrl
    document.body.appendChild(input)
    input.select()
    input.setSelectionRange(0, 99999)
    document.execCommand('copy')
    document.body.removeChild(input)
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 1500)
  }

  const handleRedirectSOSButtonClick = () => {
    window.location.href = order.cutout_link
  }

  const lineItems = order.line_items.filter((item) => item.sort_order === 0)

  return (
    <AppLayout>
      <Container>
        <Row>
          <Col md={6}>
            <h1>Thank You</h1>
            <p className='lead mb-5'>
              Your order has been successfully processed. On behalf of{' '}
              <strong className='small heavyset'>{fundraiser.title}</strong>, we
              sincerely thank you for your support.
            </p>
          </Col>
        </Row>
        <Row>
          <Col lg={6} className='mb-4 mb-lg-0'>
            <Card className='mb-3'>
              <Card.Body>
                <Card.Title>Order Details</Card.Title>
                <Card.Text>
                  Please review your order details below. A copy of your receipt
                  has been emailed to you.
                </Card.Text>
                <div>
                  <Row>
                    <Col md={6} className='my-3'>
                      <h6 className='text-muted'>Email</h6>
                      <div>{order.customer_email}</div>
                    </Col>
                    <Col md={6} className='my-3'>
                      <h6 className='text-muted'>Payment Method</h6>
                      <div className='d-flex align-items-center'>
                        <i
                          className={`text-info fa fa-2x fa-${
                            iconForCc(card.brand) || 'credit-card'
                          } mr-1`}
                        />
                        ****{card.last4}
                      </div>
                    </Col>
                    <Col md={6} className='my-3'>
                      <h6 className='text-muted'>Total</h6>
                      <div>
                        ${toPrice(order.payment.amount)}{' '}
                        {order.payment.currency.toUpperCase()}
                      </div>
                    </Col>
                    <Col md={6} className='my-3'>
                      <h6 className='text-muted'>Bank Descriptor</h6>
                      <div>
                        {`IGIV* ${fundraiser.title
                          .replace(/[^A-Za-z\s]/, '')
                          .toUpperCase()}`.slice(0, 22)}
                      </div>
                      <small>This will be the title of the charge on your credit card.</small>
                    </Col>
                    <Col md={6} className='my-3'>
                      <h6 className='text-muted'>{rewardClassification}s</h6>
                      <div>
                        {lineItems.map((item, index) => (
                          <div key={index}>
                            {item.quantity} x {item.description}
                          </div>
                        ))}
                      </div>
                    </Col>

                  </Row>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>
                  How do I get my {rewardClassification.toLowerCase()}s?
                </Card.Title>
                <Card.Text>
                  { order.cutout_link ? (
                    <>
                    <div className='text-center'>
                      <div className='mt-3'>
                        <Button
                          variant='primary'
                          onClick={handleRedirectSOSButtonClick}
                        >
                          Upload Photo(s) Now
                        </Button>
                      </div>
                    </div>
                    <br></br>
                    <p>
                      If you are unable to upload photo(s) at this time,
                      a link will be included in your confirmation email.
                    </p>
                    </>
                    ) : (
                    <>
                    <p>
                      You will receive an email with information on how to
                      get your {rewardClassification.toLowerCase()}s.
                    </p>
                    </>
                  )}
                  <p>
                    <p>
                      <strong>IMPORTANT:</strong> If you do not receive your email within the hour, check
                      your spam folder. You may also need to whitelist <a href="noreply@igiv.org">noreply@igiv.org</a> for the email
                      to come through.
                    </p>
                    { salesEmail ? (
                      <>
                        If you are in need of any technical assistance, please email <a href={supportEmailUrl} target="_blank">{supportEmail}</a>.
                        For all other campaign-related question, please email <a href={salesEmailUrl} target="_blank">{salesEmail}</a>.
                      </>
                    ) : (
                      <>
                        If you have any questions, please email <a href={supportEmailUrl} target="_blank">{supportEmail}</a>.
                      </>
                    )}
                  </p>
                </Card.Text>
                <Card.Title>
                  Can I share this {fundraiser.classification || 'fundraiser'} with others?
                </Card.Title>
                <Card.Text>
                  Yes! Share the link below with family and friends to help even
                  more!
                </Card.Text>
                <div className='text-center'>
                  <div className='font-weight-bold text-black-50 text-monospace'>
                    {shareUrl}
                  </div>
                  <div className='mt-3'>
                    <Button
                      variant={copied ? 'success' : 'primary'}
                      size='sm'
                      disabled={copied}
                      onClick={copyToClipboard}
                    >
                      <i
                        className={`fas fa-${
                          copied ? 'check' : 'copy'
                        } fa-fw mr-1`}
                      />
                      {copied ? 'Copied to clipboard' : 'Copy'}
                    </Button>
                  </div>
                </div>
                <div className='sharing-options'></div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </AppLayout>
  )
}

OrderDetails.propTypes = {
  fundraiser: PropTypes.object.isRequired,
  order: PropTypes.object.isRequired,
}

const mapState = (state) => ({
  fundraiser: state.fundraiser,
})

export default connect(mapState)(OrderDetails)
